import AuthenticationLayout from 'components/AuthLayout';
import Button from 'components/Button';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import PasswordInput from 'components/form/Inputs/PasswordInput';
import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { mutate } from 'swr';
import publicAPI from 'utils/publicAxiosInstance';

const Form = styled.form`
  width: min(90%, 20rem);
  margin-top: 0.5rem;
  & > p {
    margin-top: 1.5rem;
  }
  a {
    display: flex;
    color: var(--primary-700);
    &:hover {
      text-decoration: underline;
    }
  }

  button {
    margin-left: auto;
    margin-right: auto;
    margin-top: 2rem;
    padding: 0.7rem 1.1rem;
  }
`;

const ForgottenPasswordLink = styled(Link)`
  margin-top: 0.375rem;
  font-weight: 700;
  font-size: 0.9rem;
`;

const SignupLink = styled(Link)`
  margin-top: 7rem;
  font-weight: 400;
  justify-content: center;
  width: 100%;
`;

const defaultError = {
  general: false,
  email: false,
  password: false,
};

const Login: FC = () => {
  const navigate = useNavigate();

  const [state, setState] = useState({
    email: '',
    password: '',
    error: defaultError,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
      error: defaultError,
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setLoading(true);
    publicAPI
      .post(`/auth/login`, { email: state.email, password: state.password })
      .then((res) => {
        setLoading(false);
        const {
          data: { accessToken },
        } = res;
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('accessToken', accessToken);
          mutate('/auth/profile', undefined);
          navigate('/');
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err.response && err.response.status === 400) {
          const error = { ...defaultError };
          if (err.response.data.fields.includes('email')) {
            error.email = true;
          }
          if (err.response.data.fields.includes('password')) {
            error.password = true;
          }
          setState({ ...state, error: error });
        } else {
          setState({ ...state, error: { ...defaultError, general: true } });
        }
      });
  };

  return (
    <AuthenticationLayout title="Connexion" src_image="/logo_institut_principal.png">
      <Form onSubmit={handleSubmit}>
        {state.error.general && <Error>Une erreur est survenue.</Error>}
        {state.error.email && <Error>L'adresse email est incorrecte.</Error>}
        <Input
          type="email"
          value={state.email}
          label="Adresse email"
          onChange={handleChange}
          name="email"
          required
        />
        {state.error.password && <Error>Le mot de passe est incorrect.</Error>}
        <PasswordInput
          value={state.password}
          label="Mot de passe"
          onChange={handleChange}
          name="password"
          required
        />
        <ForgottenPasswordLink to="/authentication/forgotten-password">
          Mot de passe oublié
        </ForgottenPasswordLink>
        <Button color="primary" loading={loading}>
          Connexion
        </Button>
        <SignupLink to="/authentication/signup?from=/authentication/login">
          Je n'ai pas encore de compte
        </SignupLink>
      </Form>
    </AuthenticationLayout>
  );
};

export default Login;
